// modifyFilter — VQE-Edit edit-action: replace a single WHERE fragment text
// in place (preserving its surrounding combinator). Builds an SR-Edit
// `modify-where` op and delegates to `commitEdit`.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/modify/remove
// filter (WHERE / HAVING).

import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import type { BooleanClauseTarget } from '../../../../sql-resolver/types/structuralEdit';
import { commitEdit, type CommitEditCtx } from '../commitEdit';

export type ModifyFilterOptions = {
  /**
   * Optional structure used for index-bounds validation. Lets the caller
   * surface a friendlier error than SR-Edit's silent no-op for out-of-range
   * indices.
   */
  readonly structure?: QueryStructure;
  /** Target clause — `'where'` (default) or `'having'`. */
  readonly clause?: BooleanClauseTarget;
};

export async function modifyFilter(
  fragmentIndex: number,
  newText: string,
  ctx: CommitEditCtx,
  options: ModifyFilterOptions = {},
): Promise<void> {
  if (!Number.isInteger(fragmentIndex) || fragmentIndex < 0) {
    throw new Error(
      `modifyFilter: fragmentIndex must be a non-negative integer (got ${fragmentIndex})`,
    );
  }
  const trimmed = newText.trim();
  if (trimmed.length === 0) {
    throw new Error('modifyFilter: newText is empty');
  }
  const clause = options.clause ?? 'where';
  const label = clause === 'having' ? 'HAVING' : 'WHERE';
  if (options.structure) {
    const fragments = (clause === 'having' ? options.structure.having : options.structure.where) ?? [];
    if (fragments.length === 0) {
      throw new Error(`modifyFilter: no ${label} clause to modify`);
    }
    if (fragmentIndex >= fragments.length) {
      throw new Error(
        `modifyFilter: fragmentIndex ${fragmentIndex} out of range (${label} has ${fragments.length} fragment(s))`,
      );
    }
  }
  await commitEdit(
    { op: clause === 'having' ? 'modify-having' : 'modify-where', fragmentIndex, newText: trimmed },
    ctx,
  );
}
